<template>
  <div class="recommended-links-wrapper">
    <div v-if="isLoading" class="pt-5"><loading-spinner-small /></div>
    <div v-else>
      <div class="msg-wrapper">
        <div class="success-msg float-right">{{ successMsg }}</div>
      </div>
      <div v-if="isErrored" class="alert alert-error">
        {{ CONSTANTS.SERVER_ERROR_MESSAGE }}
      </div>
      <div class="rec-links-search-wrapper">
        <search-small
          :placeholder="'Search recommended bookmarks'"
          @on-keyup="handleSearch"
          @on-enter="handleSearch"
        ></search-small>
      </div>
      <div class="title">Recommended bookmarks</div>
      <div class="sub-title pb-1">Based on your org and role</div>
      <table :key="tableKey">
        <tbody>
          <tr
            v-for="recommendation in formattedRecommendations"
            :key="recommendation.fields.URL.Url"
          >
            <td class="first-column" style="width: 80%">
              {{ recommendation.fields.Title }}
            </td>
            <td style="width: 10%; text-align: right">
              <a
                :href="recommendation.fields.URL.Url"
                :target="getBookmarkTarget(recommendation.fields.URL.Url)"
              >
                View
              </a>
            </td>
            <td
              class="add-bookmark-column"
              @click="handleSaveBookmark(recommendation)"
            >
              <div class="add-text">
                <div class="add-icon d-inline-block" />
                <div class="d-inline-block">Add</div>
              </div>
            </td>
          </tr>
          <tr
            v-if="filteredRecommendations.length === 0"
            class="no-recommendations-text"
          >
            <td>
              No recommended bookmarks for you at this time. Check back again later.
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="displayShowMore" class="mt-2 float-right">
        <a @click="handleShowMore">Show more</a>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import * as CONSTANTS from "@/store/constants";

import HomebaseMixpanelMixin from "@/mixins/HomebaseMixpanelMixin";
import AutodeskOneBookmarksMixin from "@/mixins/AutodeskOneBookmarksMixin";
import LoadingSpinnerSmall from "@/components/LoadingSpinnerSmall";
import SearchSmall from "@/components/homebase/SearchSmall";

export default {
  name: "recommended-links",
  mixins: [HomebaseMixpanelMixin, AutodeskOneBookmarksMixin],
  props: ["bookmarks"],
  components: {
    LoadingSpinnerSmall,
    SearchSmall,
  },
  data() {
    return {
      CONSTANTS,
      isLoading: false,
      isErrored: false,
      tableKey: 0,
      recommendations: [],
      filteredRecommendations: [],
      successMsg: "",
      showCount: 10,
      pageSize: 10,
    };
  },
  created() {
    this.initializelist(this.bookmarks);
  },
  computed: {
    formattedRecommendations() {
      return this.filteredRecommendations.slice(0, this.showCount)
    },

    displayShowMore() {
      return (
        this.filteredRecommendations.length - this.showCount > 0
      );
    },
  },
  methods: {
    async initializelist() {
      this.isLoading = true;
      try {
        // get bookmarks
        let bmRes = await this.getAllBookmarks();
        let bookmarks = this.filterBookmarks(bmRes);

        // get recommendations 
        let res = await api.getRecommendations();

        let recommendations = res.value;
        let alreadyBookmarkedUrls = new Set(bookmarks.map((b) => b.url));
        this.recommendations = recommendations.filter((r) => {
          return !alreadyBookmarkedUrls.has(r.fields.URL.Url);
        });

        this.setFilteredRecommendations(this.recommendations);

      } catch (e) {
        this.isErrored = true;
      } finally {
        this.isLoading = false;
      }
    },

    handleSaveBookmark(recommendation) {
      api
        .saveBookmark(
          recommendation.fields.Title,
          recommendation.fields.URL.Url,
          recommendation.fields.SourceType,
          null,
          false
        )
        .then((response) => {
          let bookmarkData = response.data;
          this.setSuccessMessage(
            `${bookmarkData.title} saved to your bookmarks.`
          );
          window.scrollTo({ top: 0, behavior: "smooth" });
          this.removeRecommendedLink(bookmarkData);
          this.trackRecommendedLinkAdded(
            `${bookmarkData.title} - ${bookmarkData.url}`
          );
        })
        .catch((e) => {
          this.isErrored = true;
        });
    },
    removeRecommendedLink(bookmarkData) {
      // remove from original data
      this.recommendations = this.recommendations.filter(
        (r) => r.fields.URL.Url != bookmarkData.url
      );
      // remove from filtered data
      let filteredRecs = this.filteredRecommendations.filter(
        (r) => r.fields.URL.Url != bookmarkData.url
      );
      this.setFilteredRecommendations(filteredRecs);
    },

    setSuccessMessage(msg) {
      if (msg.length > 0) {
        this.successMsg = msg;
        setTimeout(() => {
          if (this.successMsg === msg) this.successMsg = "";
        }, 5000);
      }
    },

    handleShowMore() {
      this.showCount += this.pageSize;
    },

    handleSearch(payload) {
      if (payload && payload.length > 0) {
        let searchTerm = payload.toLowerCase();
        let recs = this.recommendations.filter((r) => {
          return r.fields.Title.toLowerCase().includes(searchTerm);
        });
        this.setFilteredRecommendations(recs);
      } else {
        this.setFilteredRecommendations(this.recommendations);
      }
    },

    setFilteredRecommendations(recs) {
      this.filteredRecommendations = recs;
      ++this.tableKey;
    },
  },
};
</script>

<style scoped>
.recommended-links-wrapper {
  min-height: 400px;
}
a {
  font-family: artifaktElementBold;
}
a:hover {
  cursor: pointer;
  text-decoration: none !important;
}
.title {
  font-family: artifaktElementBold !important;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 25px;
}
.sub-title {
  line-height: 20px;
  border-bottom: 1px solid #444444;
  font-size: 14px;
}

table {
  width: 100%;
}

tr {
  height: 56px !important;
  border-bottom: 1px solid #cecece;
}

.first-column {
  padding-left: 15px !important;
}
.add-text {
  font-family: artifaktElementBold !important;
  color: var(--secondary-color) !important;
}
.add-text:hover {
  color: var(--secondary-color-hover) !important;
  cursor: pointer;
}
.add-icon {
  background-image: url("../../../assets/image/homebase/bookmarks/add.svg");
  background-repeat: no-repeat;
  background-size: contain;
  width: 12px;
  height: 12px;
}
.add-text:hover .add-icon {
  cursor: pointer;
  background-image: url("../../../assets/image/homebase/bookmarks/add-hover.svg");
}
.no-recommendations-text {
  color: #666;
}
.msg-wrapper {
  min-height: 30px;
  padding: 30px 0;
}
.success-msg {
  font-family: artifaktElementBold !important;
  color: #2bc275 !important;
}
.bookmark-icon {
  color: var(--bookmark-color) !important;
  font-size: 20px;
}
.bookmark-icon:hover {
  cursor: pointer;
}
.add-bookmark-column {
  width: 10%;
  padding-left: 20px;
}
.rec-links-search-wrapper {
  padding-bottom: 36px;
}
</style>
